import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Video from "twilio-video";
import { Prompt } from 'react-router-dom';
import { Card, Spinner } from 'reactstrap';
import { Alert, Fade } from 'reactstrap';

import { ReactComponent as MicroOff } from '../../assets/images/callIcons/ic-mic-off-black.svg';
import { ReactComponent as MicroOn } from '../../assets/images/callIcons/ic-mic-on-black.svg';
import { ReactComponent as VideoOn } from '../../assets/images/callIcons/ic-video-on-black.svg';
import { ReactComponent as VideoOff } from '../../assets/images/callIcons/ic-video-off-black.svg';
import { ReactComponent as Phone } from '../../assets/images/callIcons/ic-phone-call-white.svg';
import { ReactComponent as Volume } from '../../assets/images/callIcons/ic-volume-white.svg';
import { ReactComponent as Settings } from '../../assets/images/callIcons/ic-settings-white.svg';
import { ReactComponent as Placeholder } from '../../assets/images/im-placeholders.svg';
import CallButton from './callButtons/callButton';
import toastr from '../../components/toastr';
import CallTimer from './components/timer';
import Participant from './components/participant';
import DeviceSettingsModal from "./components/DeviceSettingsModal";
import {
    PARTICIPANT_CONNECTED,
    PARTICIPANT_DISCONNECTED,
    PARTICIPANT_RECONNECTING,
    PARTICIPANT_RECONNECTED,
    RECONNECTING,
    RECONNECTED,
    CALL_COMPLETED,
    DISCONNECTED,
    MAX_VOLUME_PERCENT,
    INITIAL_VOLUME,
    MEDIA_CONNECTION_FAILED_OR_MEDIA_ACTIVITY_CEASED,
    DUPLICATE_IDENTITY,
    ACCESS_TOKEN_EXPIRED,
} from 'constants/callConstants';
import { AUDIO_TYPE } from '../../constants/communicationTypes';
import { createPlaceholderVideoTrack, isPlaceholderPublication } from './placeholderVideoTrack';
import CallsService from '../../services/calls';
import AppointmentsService from '../../services/appointments';
import dangerIcon from '../../assets/images/ic-notification-white.svg';
import closeIcon from '../../assets/images/ic-close.svg';
import Modal from "../../components/Modal";
import {
    NOTIFICATION_TYPES,
    ALERT_TEXT,
    AGREED_PRESCRIPTION,
    ALERT_COLORS,
    AGREED_DOCUMENTS,
    PRESCRIPTION_AGREED_TYPE,
} from '../../constants/callConstants';
import descSort from '../../helpers/sortFunctions/descSort';
import StorageService from '../../services/StorageService';
import {CALL_VARIABLES, USER, SESSION} from '../../constants/storageConstants';
import { BEFORE_UNLOAD } from '../../constants/windowActions';
import addNewAlert from '../../helpers/addNewAlert';
import {CLINICAL_ADMIN, SUPER_ADMIN} from "constants/roles";
import CancelModal from "components/manageSessionModal/cancelModal";


/**
 * What to tell the clinician when their connection to the call is lost. The patient is NOT
 * disconnected by any of these - only the End call button ends the call for both of them.
 */
function describeLostConnection(error) {
    switch (error?.code) {
        case DUPLICATE_IDENTITY:
            return "This call was opened in another tab or window, so it was closed here.";
        case ACCESS_TOKEN_EXPIRED:
            return "Your access to this call expired.";
        case MEDIA_CONNECTION_FAILED_OR_MEDIA_ACTIVITY_CEASED:
            return "The audio/video connection failed.";
        case 'PAGE_HIDDEN':
            return "You left this page, so you left the call.";
        default:
            return "Your connection to the call was lost.";
    }
}

export const LEAVE_PAGE_WARNING = "You are still in this call. If you leave this page the call stays open for the "
    + "patient, and you can come back to it with \"Rejoin the call\" - but anything typed in the consultation "
    + "notes will be lost. To finish the appointment, press End call instead. Leave this page?";

/**
 * The router Prompt's message. Returning true lets the navigation through without asking.
 *
 * Logout clears the session BEFORE it navigates to /login, so asking there would be a trap: cancel,
 * and the page stays with no session, where End call and Rejoin can only ever fail. The same holds for
 * any navigation once the session is gone.
 */
export function leavePageMessage(nextLocation) {
    const hasSession = Boolean(new StorageService().getValueByKey(SESSION));
    if (!hasSession || (nextLocation && nextLocation.pathname === '/login')) {
        return true;
    }
    return LEAVE_PAGE_WARNING;
}

export default function VideoComponent({ token,
    roomName,
    formRef,
    appointment,
    duration,
    isAgreedToPrescription,
    setDocumentsToFill,
    isSubmitting,
    onRejoin,
    joinAttempt = 0,
}) {
    const storedUser = new StorageService().getValueByKey(USER);
    const userRole = storedUser?.role;
    const ownIdentity = storedUser?.id !== undefined && storedUser?.id !== null ? String(storedUser.id) : null;
    const isUserClinicalAdminOrSuperAdmin =  (userRole === CLINICAL_ADMIN) || (userRole === SUPER_ADMIN);

    const [room, setRoom] = useState(null);
    const [participant, setParticipant] = useState(null);
    const [microMuted, setMicroMuted] = useState(false);
    const [videoMuted, setVideoMuted] = useState(false);
    // The blank tile published on phone appointments (see the AUDIO_TYPE effect); held so every
    // mute path can recognise it by identity, and so it can be stopped wherever it is replaced.
    const placeholderTrackRef = useRef(null);
    const placeholderStopRef = useRef(null);
    const [newSpeakers, setNewSpeakers] = useState(null);
    const [isOpenDeviceSettings, toggleDeviceSettings] = useState(false);
    const [isCallEnded, setCallEnded] = useState(false);
    const [localVideo, setLocalVideo] = useState(null);
    const [selectedDevices, setSelectedDevices] = useState({})
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);
    const [volume, setVolume] = useState(INITIAL_VOLUME);
    const [isLoading, setIsLoading] = useState(true);
    const [alerts, setAlerts] = useState([]);
    const [timerDuration, setTimerDuration] = useState();
    const [isEndCallModalOpen, setIsEndCallModalOpen] = useState(false);
    // 'connecting' | 'connected' | 'reconnecting' | 'lost'. 'lost' means THIS clinician is out of
    // the call; the room, and the patient in it, are untouched.
    const [connectionState, setConnectionState] = useState('connecting');
    const [lostReason, setLostReason] = useState('');
    // null | 'reconnecting' | 'left'
    const [patientState, setPatientState] = useState(null);
    const [isRejoining, setIsRejoining] = useState(false);
    const [isEnding, setIsEnding] = useState(false);

    const savedValues = useMemo(() => new StorageService().getValueByKey(CALL_VARIABLES), []);

    const roomRef = useRef(null);
    const endedRef = useRef(false);
    const startedRef = useRef(false);
    // The listeners THIS component put on each room. Only these come off when we leave: twilio-video
    // hangs its own cleanup on the room's 'disconnected' event (Insights publisher, local participant),
    // and removeAllListeners() would strip that too and leak it for the life of the tab.
    const ourListenersRef = useRef(new WeakMap());

    /**
     * Leave the room on THIS device only. Our listeners come off first, so our own disconnect is never
     * mistaken for a dropped connection. Never ends the call for the patient.
     */
    const leaveRoomLocally = useCallback((roomToLeave) => {
        if (!roomToLeave) {
            return;
        }
        (ourListenersRef.current.get(roomToLeave) || []).forEach(([event, handler]) => {
            roomToLeave.removeListener(event, handler);
        });
        ourListenersRef.current.delete(roomToLeave);
        try {
            stopDevices(roomToLeave.localParticipant.tracks);
            if (roomToLeave.state !== 'disconnected') {
                roomToLeave.disconnect();
            }
        } catch {
            //empty
        }
    }, []);

    /** The call is over for everyone - the room was completed, by us or by the patient. */
    const finishCall = useCallback(() => {
        if (endedRef.current) {
            return;
        }
        endedRef.current = true;
        leaveRoomLocally(roomRef.current);
        roomRef.current = null;
        setRoom(null);
        setCallEnded(true);
        AppointmentsService.updateAppointmentActualEndsTime(appointment.id, new Date()).catch(() => {});
    }, [appointment.id, leaveRoomLocally]);

    /**
     * The ONLY path that ends the call for the patient: the End call button, after confirmation.
     *
     * Every other exit - navigating away, closing the tab, a dropped connection, a media failure -
     * used to call this too, because they all went through one leave function that fell through to
     * endCall. That completed the Twilio room and threw the patient out: a GP clicking the patient's
     * name to check their record ended the consultation.
     */
    const endCallForEveryone = useCallback(async () => {
        setIsEndCallModalOpen(false);
        setIsEnding(true);
        try {
            await CallsService.endCall(appointment.id);
        } catch {
            setIsEnding(false);
            toastr.error("The call could not be ended. Please try again.");
            return;
        }
        setIsEnding(false);
        finishCall();
    }, [appointment.id, finishCall]);

    const connectionLost = useCallback((error) => {
        leaveRoomLocally(roomRef.current);
        roomRef.current = null;
        setRoom(null);
        setParticipant(null);
        setPatientState(null);
        setLostReason(describeLostConnection(error));
        setConnectionState('lost');
    }, [leaveRoomLocally]);

    const rejoin = useCallback(async () => {
        setIsRejoining(true);
        try {
            // A fresh token every time: the backend mints one per join, so a rejoin never presents
            // the token that may have just expired.
            await onRejoin();
        } catch (error) {
            // An API error has already been toasted by the request layer, which rejects with the
            // response body. A network failure rejects with an Error and is shown by nobody.
            if (!error || error instanceof Error) {
                toastr.error("Could not rejoin the call. Check your connection and try again.");
            }
        } finally {
            setIsRejoining(false);
        }
    }, [onRejoin]);

    function toggleService(service, servicesArray) {
        service((prevState) => {
            !prevState ? disableDevices(servicesArray) : enableDevices(servicesArray);
            return !prevState;
        });
    }

    // The blank tile published on phone appointments is not a camera. Muting it would undo the one
    // thing it is there for - being an ENABLED video track the patient's app can detect - so every
    // mute path skips it. Identity first, name second: the name is what the SDK reports back to us,
    // and this must hold even if that ever arrives empty.
    function isPlaceholder(publication) {
        return publication.track === placeholderTrackRef.current || isPlaceholderPublication(publication);
    }

    function disableDevices(devices) {
        devices.forEach(track => !isPlaceholder(track) && track.track.disable());
    }

    function enableDevices(devices) {
        devices.forEach(track => !isPlaceholder(track) && track.track.enable());
    }

    function stopDevices(devices) {
        devices.forEach(track => track.track.stop());
    }

    /** Forget the tile and stop its canvas, its timer and its MediaStreamTrack. */
    function clearPlaceholder() {
        const stop = placeholderStopRef.current;
        placeholderTrackRef.current = null;
        placeholderStopRef.current = null;
        if (stop) {
            stop();
        }
    }

    /** The cameras this participant has published - never the blank tile. */
    function cameraPublications(localParticipant) {
        return Array.from(localParticipant.videoTracks.values())
            .filter(publication => !isPlaceholder(publication));
    }

    function stopAndUnpublish(localParticipant, publications) {
        if (!publications.length) {
            return;
        }
        publications.forEach(publication => publication.track.stop());
        localParticipant.unpublishTracks(publications.map(publication => publication.track));
    }

    /**
     * The pre-fix behaviour, used only when the tile cannot be published: a camera that is published
     * but disabled. The patient sees nothing, and their app can still eventually detect the GP - late,
     * which is what this whole change improves on, but far better than publishing no video at all,
     * which no patient app can see the GP arrive through.
     */
    async function publishDisabledCamera(localParticipant) {
        try {
            const camera = await Video.createLocalVideoTrack();
            await localParticipant.publishTrack(camera);
            camera.disable();
            setLocalVideo(camera);
        } catch (error) {
            toastr.error("The camera could not be started for this call.");
        }
    }

    /**
     * Take the GP's camera down and put the blank tile up, in that order.
     *
     * The order is not a style choice: a Twilio 'go' room caps how many tracks one participant may
     * publish (53203), so the two must never be up at once.
     */
    async function swapCameraForPlaceholder(localParticipant) {
        const cameras = cameraPublications(localParticipant);
        const placeholder = createPlaceholderVideoTrack();

        if (!placeholder) {
            // No canvas capture available. Fall back to the old behaviour rather than leaving a live
            // camera published: a late-detected GP is better than an unexpected one on screen.
            disableDevices(cameras);
            return;
        }

        stopAndUnpublish(localParticipant, cameras);
        placeholderTrackRef.current = placeholder.track;
        placeholderStopRef.current = placeholder.stop;
        setLocalVideo(null);

        try {
            await localParticipant.publishTrack(placeholder.track);
        } catch (error) {
            // The tile never went up and the camera is already gone, so put a camera back rather than
            // leaving this call with no video track at all.
            clearPlaceholder();
            await publishDisabledCamera(localParticipant);
        }
    }

    const participantConnected = useCallback(
        async (participant) => {
            // A leftover connection under this clinician's own identity (a tab that died, a connect
            // that landed late) is never "the patient".
            if (ownIdentity !== null && String(participant.identity) === ownIdentity) {
                return;
            }
            setParticipant(participant);
            setPatientState(null);
            // Once per page. The patient reconnecting, or this clinician rejoining after a dropped
            // connection, fires this again for the same consultation - and must not restart the
            // countdown or move the recorded start time to the moment of the rejoin.
            if (startedRef.current) {
                return;
            }
            startedRef.current = true;
            if (savedValues) {
                setTimerDuration(savedValues.timer);
            } else {
                setTimerDuration(Date.now() + duration);
            }
            await AppointmentsService.updateAppointmentActualStartsTime(appointment.id, new Date())
        },
        [duration, savedValues, appointment.id, ownIdentity],
    )

    const participantDisconnected = useCallback((leaving) => {
        if (ownIdentity !== null && String(leaving.identity) === ownIdentity) {
            return;
        }
        setParticipant(current => (current === leaving ? null : current));
        setPatientState('left');
    }, [ownIdentity]);

    // The room's listeners are attached once per connection. They read the handlers through a ref, so
    // a handler changing identity never tears down and re-opens the connection.
    const handlersRef = useRef({});
    handlersRef.current = { participantConnected, participantDisconnected, finishCall, connectionLost, leaveRoomLocally };

    function detachTracks(publications, participant) {
        const tracks = Array.from(publications).map(
            (trackPublication) => {
                return trackPublication.track;
            }
        );
        participant.unpublishTracks(tracks);
    }

    function updateVideoDevice(deviceId) {
        const localParticipant = room.localParticipant;

        // On a phone appointment with the camera off, the published video IS the blank tile. Swapping
        // a camera in here would unpublish it - undoing the arrival fix for the rest of the call,
        // because nothing puts it back - and open the GP's camera on an audio consultation. The
        // camera button is the way to turn video on; a camera choice made while it is off is ignored.
        if (appointment.communicationType === AUDIO_TYPE && videoMuted) {
            return;
        }

        detachTracks(cameraPublications(localParticipant), localParticipant);

        Video.createLocalVideoTrack({
            deviceId: { exact: deviceId }
        }).then(async (localVideoTrack) => {
            const newVideo = await localParticipant.publishTrack(localVideoTrack);
            if (videoMuted) {
                disableDevices([newVideo])
            }
            setLocalVideo(localVideoTrack)
        });
    }

    function updateAudioDevice(deviceId) {
        const localParticipant = room.localParticipant;
        detachTracks(localParticipant.audioTracks.values(), localParticipant);

        Video.createLocalAudioTrack({
            deviceId: { exact: deviceId }
        }).then(async (localAudioTrack) => {
            const newAudio = await localParticipant.publishTrack(localAudioTrack);
            if (microMuted) {
                disableDevices([newAudio])
            }
        });
    }

    // Connects on mount, and again on every rejoin (a rejoin brings a fresh token and bumps
    // joinAttempt). The cleanup leaves whichever room this run joined - on unmount, and before a
    // rejoin replaces it - and only ever LOCALLY.
    useEffect(() => {
        if (endedRef.current) {
            return undefined;
        }

        let cancelled = false;
        let joined = null;

        setIsLoading(true);
        setConnectionState('connecting');

        const connecting = Video.connect(token, {
            name: roomName,
        });

        connecting.then(newRoom => {
            if (cancelled) {
                // The page was left, or a rejoin started, while this was still connecting.
                leaveRoomLocally(newRoom);
                return;
            }
            joined = newRoom;
            roomRef.current = newRoom;
            // A new room has new tracks on the default devices. A device picked in the previous room is
            // gone with it (its track was stopped when we left), so start clean rather than attach a
            // dead camera to the self-view or show a device that is not the one in use.
            setLocalVideo(null);
            setSelectedDevices(previous => (previous.speaker ? { speaker: previous.speaker } : {}));
            setRoom(newRoom);
            setIsLoading(false);
            setConnectionState('connected');
            const ours = [
                [PARTICIPANT_CONNECTED, (p) => handlersRef.current.participantConnected(p)],
                [PARTICIPANT_DISCONNECTED, (p) => handlersRef.current.participantDisconnected(p)],
                [PARTICIPANT_RECONNECTING, () => setPatientState('reconnecting')],
                [PARTICIPANT_RECONNECTED, () => setPatientState(null)],
                [RECONNECTING, () => setConnectionState('reconnecting')],
                [RECONNECTED, () => setConnectionState('connected')],
                [DISCONNECTED, (disconnectedRoom, error) => {
                    handlersRef.current.leaveRoomLocally(disconnectedRoom);
                    if (error?.code === CALL_COMPLETED) {
                        // The room was completed - by our own End call, or by the patient ending it.
                        handlersRef.current.finishCall();
                        return;
                    }
                    handlersRef.current.connectionLost(error);
                }],
            ];
            ours.forEach(([event, handler]) => newRoom.on(event, handler));
            ourListenersRef.current.set(newRoom, ours);
            newRoom.participants.forEach((p) => handlersRef.current.participantConnected(p));
        })
            .catch(error => {
                if (cancelled) {
                    return;
                }
                setIsLoading(false);
                setLostReason(`${describeLostConnection(error)} ${error?.message || ''}`.trim());
                setConnectionState('lost');
            });

        return () => {
            cancelled = true;
            // Still connecting: cancel it, or it may land later under this clinician's identity and
            // throw the NEXT connection out with 53205.
            if (!joined && typeof connecting.cancel === 'function') {
                connecting.cancel();
            }
            if (joined) {
                leaveRoomLocally(joined);
                if (roomRef.current === joined) {
                    roomRef.current = null;
                }
            }
        };
    }, [roomName, token, joinAttempt, leaveRoomLocally]);

    // Closing or reloading the tab. Leave promptly so the patient sees the clinician go at once,
    // rather than after Twilio's signalling timeout - and, again, without ending the call.
    //
    // If the browser keeps the page in its back/forward cache and later restores it, the call screen
    // would otherwise come back over a room this page has already left - so leave through the same
    // path as a dropped connection, which shows the Rejoin card.
    useEffect(() => {
        const onPageHide = () => {
            if (roomRef.current && !endedRef.current) {
                handlersRef.current.connectionLost({ code: 'PAGE_HIDDEN' });
            }
        };
        window.addEventListener('pagehide', onPageHide);
        return () => window.removeEventListener('pagehide', onPageHide);
    }, []);

    function selectDevices(video, audio, speaker) {
        const newDevices = {};
        if (video) {
            updateVideoDevice(video);
            newDevices.video = video;
        }
        if (audio) {
            updateAudioDevice(audio);
            newDevices.audio = audio;
        }
        if (speaker) {
            setNewSpeakers(speaker);
            newDevices.speaker = speaker;
        }
        setSelectedDevices(prevState => ({
            ...prevState,
            ...newDevices,
        }))
    }

    // Phone appointments: publish a BLANK tile in place of the GP's camera.
    //
    // This used to just disable the camera track, and that is why patients on the Flutter app could
    // sit in the waiting room after the GP had joined. Flutter only reacts to a GP video track that
    // is ENABLED when it subscribes, and the disable usually won that race; the patient then waited
    // for Flutter's 2-minute refresh, or gave up and pressed back. A blank track that stays enabled
    // is detected at once, shows the patient nothing, and lets the camera be stopped outright.
    useEffect(() => {
        if (!room || appointment.communicationType !== AUDIO_TYPE) {
            return undefined;
        }
        setVideoMuted(true);
        swapCameraForPlaceholder(room.localParticipant);
        return () => {
            clearPlaceholder();
        };
    }, [room, appointment.communicationType]);

    /**
     * The camera button on a PHONE appointment, where there is no camera published to enable - the
     * blank tile is up instead. Enabling nothing while the icon says "camera on" would be a lie, so
     * this really swaps: tile down, camera up, and back again. Sequential, for the same track-limit
     * reason as the initial swap.
     */
    async function toggleCameraOnPhoneCall() {
        const localParticipant = room.localParticipant;

        if (!videoMuted) {
            setVideoMuted(true);
            await swapCameraForPlaceholder(localParticipant);
            return;
        }

        const tile = placeholderTrackRef.current;
        if (tile) {
            localParticipant.unpublishTracks([tile]);
            clearPlaceholder();
        }

        try {
            const camera = await Video.createLocalVideoTrack();
            await localParticipant.publishTrack(camera);
            setLocalVideo(camera);
            setVideoMuted(false);
        } catch (error) {
            toastr.error("The camera could not be turned on.");
            await swapCameraForPlaceholder(localParticipant);
        }
    }

    // A rejoin publishes brand-new tracks, which start enabled. Carry the clinician's mute choices
    // over, so a rejoin can never silently switch their microphone or camera back on.
    useEffect(() => {
        if (!room) {
            return;
        }
        if (microMuted) {
            disableDevices(room.localParticipant.audioTracks);
        }
        if (videoMuted) {
            disableDevices(room.localParticipant.videoTracks);
        }
        // Only when the room changes: toggling mute is handled where it happens. (No rule-specific
        // eslint-disable here - the build's lint config does not load react-hooks, and naming an
        // unknown rule is itself a build-breaking error.)
    }, [room]);

    const getMessages = useCallback(
        async () => {
            const firebase = await import('../../services/firebase');
            const messaging = firebase.default.messaging();
            messaging.onMessage((payload) => {
                const newNotification = NOTIFICATION_TYPES[payload.data.type];
                addNewAlert(setAlerts, newNotification);
                if (AGREED_DOCUMENTS.includes(newNotification)) {
                    setDocumentsToFill(prevState => [...prevState, payload.data.type]
                        .sort(descSort));
                }
            });
        },
        [setDocumentsToFill],
    );

    useEffect(() => {
        getMessages();
    }, [getMessages])

    useEffect(() => {
        if (isAgreedToPrescription) {
            addNewAlert(setAlerts, AGREED_PRESCRIPTION);
            setDocumentsToFill(prevState => [...prevState, PRESCRIPTION_AGREED_TYPE]);
        }
    }, [isAgreedToPrescription, setDocumentsToFill])

    const saveValuesHandler = useCallback(
        () => {
            new StorageService().addValueByKey(CALL_VARIABLES, {
                timer: timerDuration,
            })
        },
        [timerDuration],
    );

    useEffect(() => {
        window.addEventListener(BEFORE_UNLOAD, saveValuesHandler);
        return () => {
            window.removeEventListener(BEFORE_UNLOAD, saveValuesHandler);
        }
    }, [saveValuesHandler]);


    const isCallOpen = !isCallEnded;

    return (
        <>
            <Prompt when={isCallOpen} message={leavePageMessage} />
            {isCallOpen && connectionState === 'lost' &&
                <Card className="w-100 h-100 d-flex flex-column justify-content-center align-items-center p-4 text-center">
                    <label className="call-ended-label mb-2">You are not connected to the call</label>
                    <p className="mb-1">{lostReason}</p>
                    <p>This did not end the call for the patient. Rejoin to continue, or end the call for both of you.</p>
                    <button
                        className="btn btn-primary mt-3"
                        onClick={rejoin}
                        disabled={isRejoining || isEnding}
                    >
                        {isRejoining ? <Spinner color="light" size="sm" /> : "Rejoin the call"}
                    </button>
                    <button
                        className="btn btn-outline-danger mt-2"
                        onClick={() => setIsEndCallModalOpen(true)}
                        disabled={isEnding}
                    >
                        End call
                    </button>
                </Card>
            }
            {!isCallEnded ?
                (room &&
                    <section className={"position-relative mb-4 " + (!participant && 'w-100 h-100')}>
                        {(connectionState === 'reconnecting' || patientState === 'reconnecting') &&
                            <Alert
                                color="warning"
                                className="mb-0"
                                style={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 5 }}
                            >
                                {connectionState === 'reconnecting'
                                    ? "Your connection dropped - reconnecting..."
                                    : "The patient's connection dropped - they are reconnecting..."}
                            </Alert>
                        }
                        {participant ?
                            <>
                                <Participant
                                    participant={participant}
                                    classname="remote-participant back-color-call"
                                    patient={appointment}
                                    newSpeakers={newSpeakers}
                                    volume={volume}
                                />
                                <section className="call-timer-section">
                                    <div className="call-red-oval" />
                                    <section className="call-timer ml-3">
                                        <CallTimer
                                            duration={timerDuration}
                                            setAlerts={setAlerts}
                                            alerts={alerts}
                                            isCA={isUserClinicalAdminOrSuperAdmin}
                                        />
                                    </section>
                                </section>
                            </>
                            :
                            <section className="w-100 h-100 back-color-call">
                                <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100 text-center px-4">
                                    {patientState === 'left' ?
                                        <>
                                            <label>The patient has left the call.</label>
                                            <label>The call stays open so they can rejoin. Press End call when the appointment is finished.</label>
                                        </>
                                        :
                                        <label>Patient will be with you shortly</label>
                                    }
                                </div>
                            </section>
                        }
                        <Participant
                            participant={room.localParticipant}
                            patient={appointment}
                            isLocal
                            localVideo={localVideo}
                            classname="call-small-picture off-camera-placeholder d-flex align-items-center justify-content-center "
                            volume={volume}
                            videoMuted={videoMuted}
                        />
                        <section className="call-buttons-section w-100">
                            <CallButton classname="call-button-transparent ml-5" component={<Volume />} clickAction={() => setShowVolumeSlider(prevState => !prevState)} />
                            <div>
                                <CallButton
                                    classname="call-button-white"
                                    clickAction={() => (appointment.communicationType === AUDIO_TYPE
                                        ? toggleCameraOnPhoneCall()
                                        : toggleService(setVideoMuted, room.localParticipant.videoTracks))}
                                    component={!videoMuted ? <VideoOn /> : <VideoOff />}
                                />
                                <CallButton classname="call-button-red" component={<Phone />} clickAction={() => !isEnding && setIsEndCallModalOpen(true)} />
                                <CallButton classname="call-button-white" clickAction={() => toggleService(setMicroMuted, room.localParticipant.audioTracks)} component={microMuted ? <MicroOff /> : <MicroOn />} />
                            </div>
                            <CallButton classname="call-button-transparent right mr-5" component={<Settings />} clickAction={() => toggleDeviceSettings(true)} />
                        </section>
                        {showVolumeSlider &&
                            <input
                                type="range"
                                className="custom-range volume-slider"
                                id="customRange1"
                                onBlur={() => setShowVolumeSlider(false)} onChange={(event) => setVolume(event.target.value / MAX_VOLUME_PERCENT)}
                                value={volume * MAX_VOLUME_PERCENT}
                            />
                        }
                        <section className="call-notifications">
                            {alerts.map((alert, index) => (
                                <Fade key={index}>
                                    <Alert
                                        className={
                                            "alert-dismissible fade show custom-notification ml-4 position-relative call-notifications__"
                                            +
                                            ALERT_COLORS[alert]
                                        }
                                        isOpen={true}
                                        toggle={() => setAlerts(prevState => prevState.filter((value, oldIndex) => oldIndex !== index))}
                                    >
                                        <img src={dangerIcon} alt='info' className='ml-2 position-absolute' />
                                        <p className="ml-5 mb-0 mr-2">{ALERT_TEXT[alert]}</p>
                                        <img
                                            src={closeIcon}
                                            alt='close'
                                            className='mr-2 custon-notification-two-minutes__close'
                                            onClick={() => setAlerts(prevState => prevState.filter((value, oldIndex) => oldIndex !== index))}
                                        />
                                    </Alert>
                                </Fade>
                            ))}
                        </section>
                        <Modal
                            isOpen={isOpenDeviceSettings}
                            className={"device-settings-popup"}
                        >
                            <DeviceSettingsModal
                                onClose={() => {
                                    toggleDeviceSettings(false)
                                }}
                                selectDevices={selectDevices}
                                selectedDevices={selectedDevices}
                            />
                        </Modal>
                    </section>
                )
                :
                <Card className="w-100 h-100 d-flex flex-column justify-content-center align-items-center">
                    <label className="call-ended-label mb-2">Call Ended</label>
                    <Placeholder />
                    <button
                        className="btn btn-outline leave-consultation-button mt-5"
                        onClick={() => formRef.current.submit()}
                        disabled={isSubmitting}
                    >
                        { isSubmitting && (
                            <div className="submitting">
                                <Spinner color="light" size="sm" className="text-center"/>
                            </div>
                        )}
                        { !isSubmitting && "Leave Consultation" }
                    </button>
                </Card>
            }
            {isLoading &&
                <div className="patients-table d-flex justify-content-center align-items-center">
                    <Spinner className="mx-auto" color="info" />
                </div>
            }
            {isEndCallModalOpen && (
                <CancelModal
                    visibleModal={isEndCallModalOpen}
                    setVisibleModal={() => setIsEndCallModalOpen(false)}
                    handleClose={endCallForEveryone}
                    text="Are you sure you want to end the appointment? This ends the call for you and the patient."
                    header="End call"
                />
            )}
        </>
    );
}
